import { useMemo, useState, type FormEvent } from 'react';
import { Avatar, Badge, Card } from '../../components/ui/Primitives';
import { Dialog } from '../../components/ui/Dialog';
import { useApp } from '../../lib/app/AppContext';
import { useToast } from '../../components/ui/ToastProvider';
import type { AppMessage } from '../../lib/app/store';

type Folder = 'Inbox' | 'Starred' | 'Sent' | 'Drafts' | 'Archive';

type ComposerState = {
  open: boolean;
  recipient: string;
  subject: string;
  body: string;
  replyToId?: string;
};

const closedComposer: ComposerState = {
  open: false,
  recipient: '',
  subject: '',
  body: '',
};

function timeLabel(value: string) {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function InboxPage() {
  const { pushToast } = useToast();
  const {
    messages,
    markMessageRead,
    toggleMessageStar,
    archiveMessage,
    restoreMessage,
    sendMessage,
    saveDraft,
  } = useApp();
  const [folder, setFolder] = useState<Folder>('Inbox');
  const [selectedId, setSelectedId] = useState<string | null>(messages[0]?.id ?? null);
  const [composer, setComposer] = useState<ComposerState>(closedComposer);

  const visible = useMemo(() => {
    if (folder === 'Starred') return messages.filter((message) => message.starred && message.folder !== 'archive');
    return messages.filter((message) => message.folder === folder.toLowerCase());
  }, [folder, messages]);

  const selected =
    messages.find((message) => message.id === selectedId) ??
    visible[0] ??
    null;

  const thread = selected
    ? messages
      .filter((message) => message.threadId === selected.threadId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    : [];

  const unreadCount = messages.filter((message) => message.folder === 'inbox' && message.unread).length;

  function selectMessage(message: AppMessage) {
    setSelectedId(message.id);
    if (message.unread) markMessageRead(message.id);
  }

  function openCompose() {
    setComposer({ open: true, recipient: '', subject: '', body: '' });
  }

  function openReply(message: AppMessage) {
    setComposer({
      open: true,
      recipient: message.sender,
      subject: message.subject.startsWith('Re:') ? message.subject : 'Re: ' + message.subject,
      body: '',
      replyToId: message.id,
    });
  }

  function submitMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!composer.recipient.trim() || !composer.subject.trim() || !composer.body.trim()) return;
    sendMessage({
      recipient: composer.recipient,
      subject: composer.subject,
      body: composer.body,
      replyToId: composer.replyToId,
    });
    setComposer(closedComposer);
    setFolder('Sent');
    pushToast('Message sent', { message: composer.subject, tone: 'success' });
  }

  function storeDraft() {
    if (!composer.recipient.trim() && !composer.subject.trim() && !composer.body.trim()) {
      setComposer(closedComposer);
      return;
    }
    saveDraft({
      recipient: composer.recipient,
      subject: composer.subject,
      body: composer.body,
    });
    setComposer(closedComposer);
    setFolder('Drafts');
    pushToast('Draft saved', { message: composer.subject || 'Untitled draft', tone: 'info' });
  }

  return (
    <div className="page-stack">
      <div className="hero-row">
        <div>
          <span className="page-kicker">Communications</span>
          <h1>Team messaging</h1>
          <p>Persistent threads, drafts, replies, starring and archiving in one operational inbox.</p>
        </div>
        <button className="btn primary" onClick={openCompose}>Compose</button>
      </div>

      <Card className="mail-workspace">
        <aside className="mail-folders">
          {(['Inbox', 'Starred', 'Sent', 'Drafts', 'Archive'] as Folder[]).map((item) => {
            const count = item === 'Inbox'
              ? unreadCount
              : item === 'Starred'
                ? messages.filter((message) => message.starred && message.folder !== 'archive').length
                : messages.filter((message) => message.folder === item.toLowerCase()).length;
            return (
              <button className={folder === item ? 'active' : ''} onClick={() => setFolder(item)} key={item}>
                <span>{item}</span>{count ? <Badge tone={item === 'Inbox' ? 'info' : 'neutral'}>{count}</Badge> : null}
              </button>
            );
          })}
        </aside>

        <div className="message-list">
          {visible.length ? visible.map((message) => (
            <button
              className={message.unread ? 'message unread' : 'message'}
              onClick={() => selectMessage(message)}
              key={message.id}
            >
              <Avatar name={folder === 'Sent' ? message.recipient : message.sender} size="sm" />
              <div>
                <div className="message-line">
                  <strong>{folder === 'Sent' ? 'To: ' + message.recipient : message.sender}</strong>
                  <span>{timeLabel(message.createdAt)}</span>
                </div>
                <b>{message.subject}</b>
                <p>{message.body}</p>
              </div>
              <span className="message-meta">
                {message.starred ? <i aria-label="Starred">★</i> : null}
                <Badge>{message.label}</Badge>
              </span>
            </button>
          )) : (
            <div className="empty-state">
              <strong>{folder} is empty</strong>
              <span>{folder === 'Drafts' ? 'Saved drafts will appear here.' : 'Nothing needs your attention in this folder.'}</span>
            </div>
          )}
        </div>

        <section className="message-reader">
          {selected ? (
            <>
              <header>
                <div>
                  <span className="page-kicker">{selected.label}</span>
                  <h2>{selected.subject}</h2>
                  <p>{selected.sender} → {selected.recipient} · {timeLabel(selected.createdAt)}</p>
                </div>
                <div className="message-actions">
                  <button className="icon-btn" onClick={() => { toggleMessageStar(selected.id); pushToast(selected.starred ? 'Star removed' : 'Message starred', { tone: 'info' }); }} title="Toggle star">
                    {selected.starred ? '★' : '☆'}
                  </button>
                  {selected.folder === 'archive' ? (
                    <button className="btn ghost" onClick={() => { restoreMessage(selected.id); pushToast('Message restored', { tone: 'success' }); }}>Restore</button>
                  ) : (
                    <button className="btn ghost" onClick={() => { archiveMessage(selected.id); pushToast('Message archived', { tone: 'info' }); }}>Archive</button>
                  )}
                  <button className="btn secondary" onClick={() => openReply(selected)}>Reply</button>
                </div>
              </header>

              <div className="thread-stream">
                {thread.map((message) => (
                  <article className={message.folder === 'sent' ? 'thread-message outgoing' : 'thread-message'} key={message.id}>
                    <Avatar name={message.sender} size="sm" />
                    <div>
                      <div><strong>{message.sender}</strong><span>{timeLabel(message.createdAt)}</span></div>
                      <p>{message.body}</p>
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state"><strong>Select a message</strong><span>Conversation details will appear here.</span></div>
          )}
        </section>
      </Card>

      <Dialog open={composer.open} title={composer.replyToId ? 'Reply to thread' : 'Compose message'} onClose={storeDraft}>
        <form className="form-stack" onSubmit={submitMessage}>
          <label>To<input value={composer.recipient} onChange={(event) => setComposer((current) => ({ ...current, recipient: event.target.value }))} required /></label>
          <label>Subject<input value={composer.subject} onChange={(event) => setComposer((current) => ({ ...current, subject: event.target.value }))} required /></label>
          <label>Message<textarea rows={8} value={composer.body} onChange={(event) => setComposer((current) => ({ ...current, body: event.target.value }))} required /></label>
          <div className="composer-actions">
            <button className="btn ghost" type="button" onClick={storeDraft}>Save draft</button>
            <button className="btn primary" type="submit">Send message</button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
